"use client";

import { useState, useRef, useCallback, useEffect, type ReactNode } from "react";
import type { StreamingResponse, ProviderInfo } from "@/app/lib/types";
import ResponseCard from "./ResponseCard";

interface ResponsePagerProps {
  responses: Record<string, StreamingResponse>;
  providerOrder: string[];
  providers: ProviderInfo[];
  isStreaming: boolean;
  mode?: string;
  onRetry?: (providerId: string) => Promise<void>;
  /** Prepended as the leftmost page. Used for the v1.1 auto-judge synthesis
   *  card (verdict.auto === true). When set, the pager renders N+1 pages and
   *  the synthesis card is page 0. */
  synthesisCard?: ReactNode;
  /** Provider ID for the synthesis page dot color. Falls back to accent. */
  synthesisDotColor?: string;
  /** S5 — page state-carry across active→historical archive. Caller
   *  passes the last-known page; we initialize activeIndex to it and
   *  report subsequent changes via `onPageChange`. Without these, an
   *  archived record always re-mounts at page 0 even if the user was
   *  viewing the synthesis or a different provider during streaming. */
  initialPage?: number;
  onPageChange?: (page: number) => void;
  // TTS read-aloud — see ResponseGrid's props for semantics.
  recordId?: string;
  onReadAloud?: (recordId: string, providerId: string, text: string) => void;
  ttsActiveProviderId?: string | null;
  ttsProgress?: number;
  isProviderTTSCached?: (providerId: string) => boolean;
}

const SYNTHESIS_KEY = "__synthesis__";
const GAP_PX = 12;
// Edge cards are flush left/right; middle cards are centered with peek slivers
// of both neighbors. Mirrors iOS PeekPagingBehavior.
// edge fraction 0.92 / middle fraction 0.85 → ~7-8% peek of each neighbor.
const EDGE_FRACTION = 0.92;
const MIDDLE_FRACTION = 0.85;

export default function ResponsePager({
  responses,
  providerOrder,
  providers,
  isStreaming,
  mode,
  onRetry,
  synthesisCard,
  synthesisDotColor,
  initialPage,
  onPageChange,
  recordId,
  onReadAloud,
  ttsActiveProviderId,
  ttsProgress,
  isProviderTTSCached,
}: ResponsePagerProps) {
  const [activeIndex, setActiveIndex] = useState(initialPage ?? 0);
  // Notify parent of page changes — used by the active→historical
  // carry-over machinery in page.tsx (S5). Lazy: only fires if a
  // callback was supplied.
  useEffect(() => {
    onPageChange?.(activeIndex);
  }, [activeIndex, onPageChange]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const orderedKeys: string[] = synthesisCard
    ? [SYNTHESIS_KEY, ...providerOrder]
    : providerOrder;
  const count = orderedKeys.length;

  // Track container width for snap-position math
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Card widths: edge cards (first/last) wider so they sit flush; middle cards
  // narrower with peek slivers. Total content width determines snap math.
  const cardWidths: number[] = orderedKeys.map((_, i) =>
    count <= 2 || i === 0 || i === count - 1
      ? containerWidth * EDGE_FRACTION
      : containerWidth * MIDDLE_FRACTION
  );

  // Card start positions in content coordinate space
  const cardStarts: number[] = (() => {
    const starts: number[] = [0];
    for (let i = 1; i < cardWidths.length; i++) {
      starts.push(starts[i - 1] + cardWidths[i - 1] + GAP_PX);
    }
    return starts;
  })();

  // Snap positions: first card flush left, last card flush right, middle
  // cards centered in viewport.
  const snapPositions: number[] = (() => {
    if (cardWidths.length === 0) return [];
    if (cardWidths.length === 1) return [0];
    const totalContent =
      cardStarts[cardStarts.length - 1] + cardWidths[cardWidths.length - 1];
    return cardWidths.map((_, i) => {
      if (i === 0) return 0;
      if (i === cardWidths.length - 1) return totalContent - containerWidth;
      const cardCenter = cardStarts[i] + cardWidths[i] / 2;
      return cardCenter - containerWidth / 2;
    });
  })();

  // Reset to first card when the page set genuinely changes (providers
  // toggled, synthesis card appears mid-flight). Must NOT fire on initial
  // mount — that would overwrite the `initialPage` carry-over from the
  // active pager (S5) and pop the historical pager back to leftmost on
  // every stream completion.
  const didMountResetRef = useRef(false);
  useEffect(() => {
    if (!didMountResetRef.current) {
      didMountResetRef.current = true;
      return;
    }
    setActiveIndex(0);
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
  }, [count, synthesisCard != null]);

  // Apply initialPage to the DOM scroll position once container width is
  // measured. useState seeds activeIndex correctly, but the scroll viewport
  // starts at scrollLeft = 0 — without this effect the user sees card 0
  // even when state says we're on card N.
  const initialScrollAppliedRef = useRef(false);
  useEffect(() => {
    if (initialScrollAppliedRef.current) return;
    if (!scrollRef.current) return;
    if (containerWidth === 0 || snapPositions.length === 0) return;
    const target = snapPositions[activeIndex] ?? 0;
    scrollRef.current.scrollLeft = target;
    initialScrollAppliedRef.current = true;
  }, [containerWidth, snapPositions, activeIndex]);

  // Track active card via scroll position
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || snapPositions.length === 0) return;
    const proposed = el.scrollLeft;
    let nearest = 0;
    let nearestDist = Math.abs(snapPositions[0] - proposed);
    for (let i = 1; i < snapPositions.length; i++) {
      const d = Math.abs(snapPositions[i] - proposed);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = i;
      }
    }
    if (nearest !== activeIndex) setActiveIndex(nearest);
  }, [snapPositions, activeIndex]);

  if (count === 0) return null;

  return (
    <div ref={containerRef}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="response-pager"
        style={{
          display: "flex",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          gap: `${GAP_PX}px`,
          scrollbarWidth: "none",
        }}
      >
        {orderedKeys.map((key, idx) => {
          const isSynthesis = key === SYNTHESIS_KEY;
          const isEdge = count <= 2 || idx === 0 || idx === count - 1;
          const cardWidth = cardWidths[idx];
          // First card flush-left, last card flush-end, middle cards centered.
          const snapAlign: "start" | "center" | "end" =
            idx === 0 ? "start" : idx === count - 1 ? "end" : "center";

          if (isSynthesis) {
            return (
              <div
                key={SYNTHESIS_KEY}
                className="pager-card"
                style={{
                  flex: "0 0 auto",
                  width: cardWidth ? `${cardWidth}px` : `${EDGE_FRACTION * 100}%`,
                  scrollSnapAlign: snapAlign,
                  minWidth: 0,
                }}
              >
                {synthesisCard}
              </div>
            );
          }

          // In-flight: an expected provider with no persisted response yet
          // renders a spinner placeholder (stable skeleton on history-revisit),
          // mirroring ResponseGrid. Complete records still drop absent ones.
          const response =
            responses[key] ??
            (isStreaming
              ? { id: "", text: "", isComplete: false, wasInterrupted: false }
              : undefined);
          const provider = providers.find((p) => p.id === key);
          if (!response) return null;
          // Prefer per-response mode set by the dispatcher (see ResponseGrid
          // for full rationale). Reverse-lookup is the fallback for older
          // records — ambiguous when one model serves multiple modes.
          const derivedMode = provider?.models_by_mode && response.model
            ? Object.entries(provider.models_by_mode).find(([, m]) => m.model === response.model)?.[0]
            : undefined;
          const cardMode = response.mode
            || (mode === "auto" ? derivedMode : mode)
            || derivedMode;

          return (
            <div
              key={key}
              className="pager-card"
              style={{
                flex: "0 0 auto",
                width: cardWidth
                  ? `${cardWidth}px`
                  : `${(isEdge ? EDGE_FRACTION : MIDDLE_FRACTION) * 100}%`,
                scrollSnapAlign: snapAlign,
                minWidth: 0,
              }}
            >
              <ResponseCard
                providerId={key}
                providerName={provider?.display_name ?? key}
                providerColor={provider?.color ?? "var(--text-secondary)"}
                providerIcon={provider?.icon}
                response={response}
                isStreaming={isStreaming && !response.isComplete}
                mode={cardMode}
                onRetry={onRetry}
                recordId={recordId}
                onReadAloud={onReadAloud}
                isTTSActive={ttsActiveProviderId === key}
                ttsProgress={ttsActiveProviderId === key ? ttsProgress : undefined}
                isTTSCached={isProviderTTSCached ? isProviderTTSCached(key) : undefined}
              />
            </div>
          );
        })}
      </div>

      {/* Page dots */}
      {count > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "6px",
            padding: "6px 0",
          }}
        >
          {orderedKeys.map((key, idx) => {
            const isActive = idx === activeIndex;
            let dotColor: string;
            if (key === SYNTHESIS_KEY) {
              dotColor = synthesisDotColor || "var(--accent)";
            } else {
              const p = providers.find((pr) => pr.id === key);
              dotColor = p?.color ?? "var(--text-secondary)";
            }
            // Force-remount the dot whenever its active state flips by
            // putting `isActive` in the React key. Inline-style transitions
            // on a single reused <button> were leaving iOS Safari's paint
            // state stuck filled even after `background` switched back to
            // transparent (`light up but never turn back hollow`). Distinct
            // keys for active vs inactive guarantee the previous DOM node
            // is unmounted and a fresh one painted from scratch.
            return isActive ? (
              <button
                key={`${key}-on`}
                onClick={() => {
                  setActiveIndex(idx);
                  const el = scrollRef.current;
                  if (el && snapPositions[idx] != null) {
                    el.scrollTo({ left: snapPositions[idx], behavior: "smooth" });
                  }
                }}
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  border: "none",
                  backgroundColor: dotColor,
                  cursor: "pointer",
                  padding: 0,
                  margin: 0,
                  opacity: 1,
                }}
                title={key === SYNTHESIS_KEY ? "Synthesis" : key}
              />
            ) : (
              <button
                key={`${key}-off`}
                onClick={() => {
                  setActiveIndex(idx);
                  const el = scrollRef.current;
                  if (el && snapPositions[idx] != null) {
                    el.scrollTo({ left: snapPositions[idx], behavior: "smooth" });
                  }
                }}
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  borderWidth: "1.5px",
                  borderStyle: "solid",
                  borderColor: "var(--text-tertiary)",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  margin: 0,
                  opacity: 0.5,
                }}
                title={key === SYNTHESIS_KEY ? "Synthesis" : key}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
